import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthState, Usuario } from '../types'
import { simpleAuthService } from '../services/simpleAuthService'
import toast from 'react-hot-toast'

interface AuthStore extends AuthState {
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: Usuario) => void
  setToken: (token: string) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true })
        try {
          // Primero obtener el token
          const tokenResponse = await simpleAuthService.login(username, password)
          
          // Luego obtener la información del usuario
          const user = await simpleAuthService.getCurrentUser(tokenResponse.access_token)
          
          set({
            user: user,
            token: tokenResponse.access_token,
            isAuthenticated: true,
            isLoading: false,
          })
          toast.success('Inicio de sesión exitoso')
        } catch (error: any) {
          set({ isLoading: false })
          console.error('Error al iniciar sesión:', error)
          let errorMessage = 'Error al iniciar sesión'
          
          if (error.response?.data?.detail) {
            if (Array.isArray(error.response.data.detail)) {
              // Si es un array de errores de validación, tomar el primer mensaje
              errorMessage = error.response.data.detail[0]?.msg || error.response.data.detail[0]?.message || errorMessage
            } else if (typeof error.response.data.detail === 'string') {
              errorMessage = error.response.data.detail
            }
          } else if (error.message) {
            errorMessage = error.message
          }
          
          toast.error(errorMessage)
          throw error
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })
        toast.success('Sesión cerrada')
      },

      setUser: (user: Usuario) => {
        set({ user })
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: true })
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },
    }),
    {
      name: 'kenzen-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
